export type TileRequest<T> = {
  tile: T;
  priority: number;
};

/**
 * Max-Heap based priority queue for tile requests.
 */
export class TilePriorityQueue<T extends { toKey(): string }> {
  private heap: TileRequest<T>[] = [];
  private keySet = new Set<string>();

  private swap(i: number, j: number) {
    [this.heap[i], this.heap[j]] = [this.heap[j], this.heap[i]];
  }

  private bubbleUp(index: number) {
    while (index > 0) {
      const parent = Math.floor((index - 1) / 2);
      if (this.heap[parent].priority >= this.heap[index].priority) {
        break;
      }
      this.swap(parent, index);
      // biome-ignore lint/style/noParameterAssign:
      index = parent;
    }
  }

  private bubbleDown(index: number) {
    const length = this.heap.length;
    while (true) {
      let largest = index;
      const left = 2 * index + 1;
      const right = 2 * index + 2;

      if (left < length && this.heap[left].priority > this.heap[largest].priority) {
        largest = left;
      }

      if (right < length && this.heap[right].priority > this.heap[largest].priority) {
        largest = right;
      }

      if (largest === index) {
        break;
      }
      this.swap(index, largest);
      // biome-ignore lint/style/noParameterAssign:
      index = largest;
    }
  }

  enqueue(request: TileRequest<T>) {
    const key = request.tile.toKey();
    if (this.keySet.has(key)) {
      return; // Deduplication
    }

    this.heap.push(request);
    this.keySet.add(key);
    this.bubbleUp(this.heap.length - 1);
  }

  dequeue(): TileRequest<T> | undefined {
    if (this.heap.length === 0) {
      return undefined;
    }
    const top = this.heap[0];
    const last = this.heap.pop();
    if (this.heap.length > 0 && last) {
      this.heap[0] = last;
      this.bubbleDown(0);
    }
    this.keySet.delete(top.tile.toKey());
    return top;
  }

  isEmpty(): boolean {
    return this.heap.length === 0;
  }

  clear() {
    this.heap = [];
    this.keySet.clear();
  }
}
